import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Settings,
  Shield,
  ChevronRight,
  Lock,
  Unlock,
  RefreshCw,
  ArrowRight,
  RotateCcw,
  KeyRound,
  CalendarClock,
  Trash2,
  FileQuestion,
  Zap,
  UserCheck,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_layout/settings/action-controls')({
  component: ActionControlsPage,
});

/* ── Action Data from Spreadsheet ─────────────────────── */

interface ActionControl {
  functionality: string;
  executionType: 'Fully Automated' | 'Manual Approval' | 'Manual Admin' | 'Automated + Manual';
  responsibleRole: string;
  expectedBehavior: string;
  auditRequired: boolean;
  icon: React.ReactNode;
}

const actionControls: ActionControl[] = [
  {
    functionality: 'Lock',
    executionType: 'Fully Automated',
    responsibleRole: 'System',
    expectedBehavior:
      'Automatically restricts device when overdue threshold is reached.',
    auditRequired: true,
    icon: <Lock className="w-4 h-4" />,
  },
  {
    functionality: 'Unlock',
    executionType: 'Fully Automated',
    responsibleRole: 'System',
    expectedBehavior:
      'Automatically restores device after payment confirmation or approved correction.',
    auditRequired: true,
    icon: <Unlock className="w-4 h-4" />,
  },
  {
    functionality: 'Remove Lock / Disengage',
    executionType: 'Automated + Manual',
    responsibleRole: 'System / Tenant Admin',
    expectedBehavior:
      'Automatically disengages financing controls after full repayment; Tenant Admin handles exceptions.',
    auditRequired: true,
    icon: <Shield className="w-4 h-4" />,
  },
  {
    functionality: 'Sync State',
    executionType: 'Fully Automated',
    responsibleRole: 'System',
    expectedBehavior:
      'Synchronizes device, payment and contract states in real time or queued mode.',
    auditRequired: true,
    icon: <RefreshCw className="w-4 h-4" />,
  },
  {
    functionality: 'Transfer',
    executionType: 'Manual Approval',
    responsibleRole: 'Branch Manager',
    expectedBehavior:
      'Approves inventory/device/customer assignment transfer; system updates ownership.',
    auditRequired: true,
    icon: <ArrowRight className="w-4 h-4" />,
  },
  {
    functionality: 'Process Return',
    executionType: 'Manual Approval',
    responsibleRole: 'Branch Manager',
    expectedBehavior:
      'Verifies returned device and system updates inventory and contract status.',
    auditRequired: true,
    icon: <RotateCcw className="w-4 h-4" />,
  },
  {
    functionality: 'Issue Offline PIN',
    executionType: 'Manual Approval',
    responsibleRole: 'Branch Manager',
    expectedBehavior:
      'Approves request; system generates secure temporary PIN.',
    auditRequired: true,
    icon: <KeyRound className="w-4 h-4" />,
  },
  {
    functionality: 'Update Expiration',
    executionType: 'Manual Admin',
    responsibleRole: 'Tenant Admin',
    expectedBehavior:
      'Updates financing expiration/contract terms under controlled workflow.',
    auditRequired: true,
    icon: <CalendarClock className="w-4 h-4" />,
  },
  {
    functionality: 'Reset State',
    executionType: 'Manual Admin',
    responsibleRole: 'Tenant Admin',
    expectedBehavior:
      'Performs exceptional recovery/reset for operational issues.',
    auditRequired: true,
    icon: <Trash2 className="w-4 h-4" />,
  },
  {
    functionality: 'Write Off',
    executionType: 'Manual Admin',
    responsibleRole: 'Tenant Admin',
    expectedBehavior:
      'Approves write-off and closes or flags contract financially.',
    auditRequired: true,
    icon: <FileQuestion className="w-4 h-4" />,
  },
];

/* ── Helpers ──────────────────────────────────────────── */

const executionTypeCfg: Record<
  string,
  { cls: string; icon: React.ReactNode }
> = {
  'Fully Automated': {
    cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    icon: <Zap className="w-3 h-3" />,
  },
  'Manual Approval': {
    cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    icon: <UserCheck className="w-3 h-3" />,
  },
  'Manual Admin': {
    cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
    icon: <Shield className="w-3 h-3" />,
  },
  'Automated + Manual': {
    cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
    icon: <RefreshCw className="w-3 h-3" />,
  },
};

/* ── Main Page ────────────────────────────────────────── */

function ActionControlsPage() {
  return (
    <div className="space-y-6">
      {/* Breadcrumb back */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/settings" className="hover:text-foreground transition-colors">
          Settings
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-foreground font-medium">Action Controls</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Device Lifecycle Action Controls
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Defines which actions are automated and which require role-based
            approval
          </p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-lg">
          <Settings className="h-5 w-5" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(executionTypeCfg).map(([type, cfg]) => (
          <Badge key={type} className={cn('gap-1.5 text-xs', cfg.cls)}>
            {cfg.icon}
            {type}
          </Badge>
        ))}
      </div>

      {/* Action Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {actionControls.map((action) => {
          const typeCfg =
            executionTypeCfg[action.executionType] ||
            executionTypeCfg['Fully Automated'];
          return (
            <Card
              key={action.functionality}
              className="shadow-md border-0 hover:shadow-lg transition-all duration-200 overflow-hidden relative"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'p-2.5 rounded-xl shadow-lg text-white',
                        action.executionType === 'Fully Automated'
                          ? 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                          : action.executionType === 'Manual Admin'
                            ? 'bg-gradient-to-br from-red-500 to-red-600'
                            : action.executionType === 'Automated + Manual'
                              ? 'bg-gradient-to-br from-violet-500 to-violet-600'
                              : 'bg-gradient-to-br from-amber-500 to-amber-600'
                      )}
                    >
                      {action.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {action.functionality}
                      </CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        {action.responsibleRole}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={cn('gap-1 shrink-0', typeCfg.cls)}>
                    {typeCfg.icon}
                    {action.executionType}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {action.expectedBehavior}
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-border/50">
                  <Badge
                    variant="outline"
                    className="text-[10px] gap-1"
                  >
                    <Shield className="w-3 h-3" />
                    Audit Required
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">
                    Role: {action.responsibleRole}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
